import type { IBasicPostData } from '@gik/api/activities/posts';
import { createPost, deletePost, updatePost } from '@gik/api/activities/posts';
import { useInkind } from '@gik/api/inkinds/inkind';
import type { UseStreamSWROptions } from '@gik/api/stream/useStream';
import { useComment, useStreamPost } from '@gik/api/stream/useStream';
import { useInkindCan } from '@gik/core/store/permissions';
import { useUserStore } from '@gik/core/store/UserStore';
import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import type { RasterAttachImageResult } from '@gik/core/utils/FileUtils';
import sleep from '@gik/core/utils/sleep';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { useInkindParticipants } from '@gik/inkind-page/utils/useInkindParticipants';
import { dateTimeFormat } from '@gik/l10n';
import KindWordsIcon from '@gik/stream/assets/kindWord.svg';
import type { PostActivity, PostMedia, PostOptions } from '@gik/stream/types/Stream';
import StreamClient from '@gik/stream/utils/StreamClient';
import { Container } from '@gik/ui/Container';
import { Heading } from '@gik/ui/Heading';
import { LoadingSpinner } from '@gik/ui/LoadingSpinner';
import { SvgIcon } from '@gik/ui/SvgIcon';
import { UI } from '@gik/ui/UIManager';
import moment from 'moment';
import React from 'react';
import type { PostInputPayload } from './PostInput';
import { StreamInputWithAvatar } from './StreamInputWithAvatar';
import { openPostInputModal } from './StreamModals';
import type {
  StreamPaginationAddItemCallback,
  StreamPaginationDeleteItemCallback,
  StreamPaginationLoadLatestItemsCallback,
  StreamPaginationReloadItemCallback,
  StreamPaginationSetErrorCallback,
  StreamPaginationUpdateItemCallback,
} from './StreamPagination';
import { StreamPagination } from './StreamPagination';
import { StreamPost } from './StreamPost';

export type StreamPostsProps = {
  perPage?: number;
  inkindRouteId: string;
  highlightedPostId?: string;
  highlightedCommentId?: string;
  isPageOwnerOrOrganizer?: boolean;
} & UIComponent;

type IPostDataForUpdate = IBasicPostData & {
  attachmentsToAdd?: RasterAttachImageResult[];
  attachmentsToRemove?: string[];
};

function StreamPostsComp({
  children,
  className,
  inkindRouteId,
  highlightedPostId,
  highlightedCommentId,
  perPage = 5,
  isPageOwnerOrOrganizer,
  ...otherProps
}: React.PropsWithChildren<StreamPostsProps>): React.ReactElement {
  const bem = useBemCN('stream-posts');

  const userId = useUserStore(state => state.id);

  const [value, setValue] = React.useState<string>();
  const participants = useInkindParticipants(inkindRouteId);
  const { data: inkind } = useInkind(inkindRouteId);
  const canEdit = useInkindCan('manage', inkindRouteId, inkind?.groupId);

  const { data: highlightedComment } = useComment(highlightedCommentId);

  /**
   * create an empty post object to optimistically add a new post
   */
  const createNewPostDataModel = React.useCallback(
    (message: string, postOptions: PostOptions): PostActivity => {
      return {
        post: {
          userId,
          pageRouteId: inkindRouteId,
          message,
          ...postOptions,
        },
        deleted: false,
        verb: 'na',
        id: 'new',
        foreign_id: 'na',
        time: moment.utc().format(dateTimeFormat),
        latest_reactions: {},
        created_date: moment.utc().format(dateTimeFormat),
        actor: userId,
        object: {},
      };
    },
    [inkindRouteId, userId]
  );

  const handleCreateSubmit = React.useCallback(
    async (
      payload,
      loadLatestItems: StreamPaginationLoadLatestItemsCallback,
      setError: StreamPaginationSetErrorCallback
    ) => {
      setError('new', null);
      UI.loadingSpinnerOverlay();

      const postId = await createPost(
        inkindRouteId,
        { message: payload.message, ...payload.options },
        payload.attachments
      );
      if (postId) {
        await sleep(500);
        loadLatestItems();
      } else {
        setError('new', 'create_fail');
      }

      UI.closeAllDialogs();
    },
    [inkindRouteId]
  );

  const handleCreate = React.useCallback(
    async (
      payload: PostInputPayload,
      addItem: StreamPaginationAddItemCallback,
      loadLatestItems: StreamPaginationLoadLatestItemsCallback,
      setError: StreamPaginationSetErrorCallback
    ) => {
      addItem(createNewPostDataModel(payload.message, payload.options));

      handleCreateSubmit(payload, loadLatestItems, setError);
    },
    [createNewPostDataModel, handleCreateSubmit]
  );

  const handleUpdateSubmit = React.useCallback(
    async (
      id: string,
      data: IPostDataForUpdate,
      reloadItem: StreamPaginationReloadItemCallback,
      setError: StreamPaginationSetErrorCallback
    ) => {
      setError(id, null);
      UI.loadingSpinnerOverlay();

      const response = await updatePost(id, data, data.attachmentsToAdd, data.attachmentsToRemove);
      if (response) {
        reloadItem(id);
      } else {
        setError(id, 'update_fail');
      }

      if (!response) setError(id, 'update_fail');

      UI.closeAllDialogs();
    },
    []
  );

  const handleUpdate = React.useCallback(
    async (
      activity: PostActivity,
      updateItem: StreamPaginationUpdateItemCallback,
      reloadItem: StreamPaginationReloadItemCallback,
      setError: StreamPaginationSetErrorCallback
    ) => {
      const handleSubmit = (payload: PostInputPayload, close: () => void) => {
        close?.();
        const postData: IPostDataForUpdate = {
          message: payload.message,
          attachmentsToAdd: payload.attachments,
          attachmentsToRemove: payload.attachmentsToRemove,
          ...payload.options,
        };

        updateItem(activity.id, {
          post: postData,
        });

        handleUpdateSubmit(activity.id, postData, reloadItem, setError);
      };

      const attachmentsEdit: PostMedia[] = Object.keys(activity?.media || {}).map(m => {
        return activity.media[m];
      });

      openPostInputModal(
        { inkindRouteId },
        {
          onSubmit: handleSubmit,
          users: participants,
          value: activity.post.message,
          backgroundColor: activity.post.backgroundColor,
          attachmentsEdit,
        },
        {
          onClose: (response: boolean) => {
            if (response === true) reloadItem(activity.id);

            return response;
          },
          title: 'Edit Message',
        }
      );
    },
    [inkindRouteId, participants, handleUpdateSubmit]
  );

  const handleDeleteSubmit = React.useCallback(
    async (
      id: string,
      deleteItem: StreamPaginationDeleteItemCallback,
      loadOlder: () => void,
      setError: StreamPaginationSetErrorCallback
    ) => {
      setError(id, null);
      UI.loadingSpinnerOverlay();

      const response = await deletePost(id, canEdit ? inkindRouteId : undefined);
      if (!response) {
        setError(id, 'delete_fail');
        return;
      }

      // delete item from UI
      deleteItem(id);

      // if this was the last visible item to be deleted and there are more items then trigger a loadOlder automatically
      loadOlder();

      UI.closeAllDialogs();
    },
    [canEdit, inkindRouteId]
  );

  // show loading spinner while we are waiting for a highlighted comment to load in
  if (highlightedCommentId && !highlightedComment) return <LoadingSpinner center />;

  return (
    <div {...bem(null, null, className)} {...otherProps}>
      <Container>
        <Heading>MESSAGES</Heading>

        <StreamPagination<PostActivity>
          id="inkind-comments"
          order="desc"
          // hasPrev
          perPage={perPage}
          highlightedId={highlightedComment?.activity_id || highlightedPostId}
          header={({ addItem, loadLatestItems: loadNewestItems, setError }) => {
            return (
              <header {...bem('stream-input-wrapper')}>
                <StreamInputWithAvatar
                  postModal
                  variant="neutral"
                  placeholder="Leave a message"
                  value={value}
                  onSubmit={payload => handleCreate(payload, addItem, loadNewestItems, setError)}
                  onChange={({ target }) => {
                    setValue(target.value);
                  }}
                  participants={participants}
                  hasBgSelector
                  hasImageUpload
                  inkindRouteId={inkindRouteId}
                />
              </header>
            );
          }}
          sanitizeData={(items: PostActivity[]) => {
            return items?.filter(item => !item?.deletedTime);
          }}
          fetch={function useFetch(paginationOptions: UseStreamSWROptions) {
            return StreamClient.getPosts(inkindRouteId, { ...paginationOptions, recentReactionsLimit: 5 });
          }}
          // fetch={function useFetch(paginationOptions: UseStreamSWROptions) {
          //   return useStreamPosts(inkindRouteId, { ...paginationOptions, recentReactionsLimit: 5 });
          // }}
          fetchSingle={function useFetch(id: string) {
            return useStreamPost(id);
          }}
          emptyDisplay={() => {
            return (
              <div {...bem('empty')}>
                <div>
                  <SvgIcon Icon={KindWordsIcon} />
                  <p>Be the first to leave a message</p>
                </div>
              </div>
            );
          }}
        >
          {({ data: items, reloadItem, updateItem, deleteItem, loadOlder, loadLatestItems, errors, setError }) => {
            return items?.map(activity => {
              return (
                <StreamPost
                  {...bem('post')}
                  key={activity.id}
                  activity={activity}
                  isTemp={activity.id?.startsWith('new')}
                  disabled={activity.id?.startsWith('new')}
                  error={errors[activity.id]}
                  participants={participants}
                  inkindRouteId={inkindRouteId}
                  isPageOwnerOrOrganizer={isPageOwnerOrOrganizer}
                  onReaction={reloadItem}
                  onDeleteReaction={reloadItem}
                  onEdit={() => handleUpdate(activity, updateItem, reloadItem, setError)}
                  onDelete={() => handleDeleteSubmit(activity.id, deleteItem, loadOlder, setError)}
                  onCreateRetry={(id: string, data: IBasicPostData) =>
                    handleCreateSubmit(data, loadLatestItems, setError)
                  }
                  onUpdateRetry={(id: string, data: IBasicPostData) =>
                    handleUpdateSubmit(id, data, reloadItem, setError)
                  }
                  onDeleteRetry={(id: string) => handleDeleteSubmit(id, deleteItem, loadOlder, setError)}
                  highlightedCommentId={highlightedCommentId}
                />
              );
            });
          }}
        </StreamPagination>
      </Container>
    </div>
  );
}

export const StreamPosts = withComponentErrorBoundary(StreamPostsComp);
